import React, {
  useEffect, useState, useCallback, useMemo, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Drawer, message } from 'antd';
import {
  EyeOutlined,
  SaveOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { FormHelpers, FormReassignmentPermissions } from 'ontraccr-common';
import { DateTime } from 'luxon';
import Axios from 'axios';

import FullPhoto from '../common/photos/FullPhoto';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import DrawerFooter from '../common/containers/DrawerFooter';
import { getFileType, downloadFile } from '../files/fileHelpers';
import SignatureModal from './SignatureModal';
import FormResponseFields from './FormResponseFields';

import ResubmitModal from './ResubmitModal';
import generatePDFPreview from './generatePDFPreview';
import FormsService from './state/forms.service';

import FormApprovalButton, {
  APPROVE_FORM_SUBMISSION, REJECT_FORM_SUBMISSION,
} from './FormApprovalButton';

import {
  submitForm,
  deleteDraft,
  clearFormSelection,
  getSharedFormDraftChildren,
  markFormInitiated,
} from './state/forms.actions';

import {
  getErrorsFromResponses,
  prepareResponsePayload,
  preparePreloadedFormTriggerData,
  formsValuesAreDifferent,
  DEFAULT_FORM_RESPONDER_MODE,
  PO_FORM_RESPONDER_MODE,
  SUB_CONTRACT_FORM_RESPONDER_MODE,
  validateTimeEntryTableResponses,
  constructFormPayloadForAPI,
} from './ResponderHelpers';
import { getIdMap, uuid } from '../helpers/helpers';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import FormReassignmentDrawer from './FormReassignmentDrawer';
import {
  applyConditionalResponseCleanup,
  getFormattedCurrency,
  getSectionPermissionMap,
  resolveFormDivisionIds,
} from './formHelpers';
import Permissions from '../auth/Permissions';
import { getUserIdMap } from '../state/selectors';
import DuplicateCardsSlider from './DuplicateCardsSlider';
import useDuplicateCardDetection from './hooks/useDuplicateCardDetection';
import DuplicateConflictButton from './DuplicateConflictButton';

const {
  ASSIGN_OWN,
  canReassignDraft,
  canReassignForm,
  getReassignmentAssignment,
  getReassignmentConfigFromTemplate,
} = FormReassignmentPermissions;

const AUTOSAVE_INTERVAL = 15000;
const CONFLICT_BUTTON_STYLE = { marginLeft: 20 };

const createTitle = ({
  mode,
  title,
  number,
  formValue,
  costToDate,
  poCloseMode,
}) => {
  let prefix = '';
  if (mode === PO_FORM_RESPONDER_MODE) {
    prefix = 'Add PO: ';
  } else if (mode === SUB_CONTRACT_FORM_RESPONDER_MODE) {
    prefix += 'Add Sub-Contract: ';
  }
  let poSuffix = '';
  if (poCloseMode) {
    const poValueCurr = getFormattedCurrency(formValue);
    const costToDateCurr = getFormattedCurrency(costToDate);
    const committedCurr = getFormattedCurrency(formValue - costToDate);
    poSuffix = ` (PO Value: ${poValueCurr}, Cost to Date: ${costToDateCurr}, Committed Cost: ${committedCurr})`;
  }
  const formTitleComp = title ?? '';
  const titleSuffix = number ? ` - ${number}` : '';
  return `${prefix}${formTitleComp}${titleSuffix}${poSuffix}`;
};

export default function FormResponder({
  visible = false,
  mode = DEFAULT_FORM_RESPONDER_MODE,
  onClose,
  history,
  redirectUrl = '/forms/log',
  assignedForm: {
    id: assignedFormId,
    draftId: assignedDraftId,
  } = {},
  isOnEditStep,
  isResubmit,
  isEdit,
  onSubmit: controlledSubmit,
  controlledData,
  controlledMode, // Used to override dispatching events from here.
  preloadedData,
  preloadedIds,
  cardId,
  equipmentId,
  draftDisabled = false,
  formSelector, // Allows user to select desired form
  lockedProjectId,
  showAsContainer = false,
  isExternalForm = false,
  previewDisabled = false,
  header,
  hideCancelButton = false,
  submitButtonStyle,
  cancelButtonStyle,
  initialFileMap,
  childSubmitMode,
  poCloseMode,
  hasLoadedPayableTemplate,
  isCopy,
}) {
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.projects.projects);
  const customers = useSelector((state) => state.customers.customers);
  const selectedFormTemplate = useSelector((state) => state.forms.selectedFormTemplate);
  const templates = useSelector((state) => state.forms.templates);
  const selectedForm = useSelector((state) => state.forms.selectedForm);
  const triggeredForms = useSelector((state) => state.forms.triggeredForms);
  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const circularFieldIds = useSelector((state) => state.forms.circularFieldIds);
  const equipment = useSelector((state) => state.equipment.equipment);
  const timeEntryCardCustomFields = useSelector((state) => state.timecards.customFields);
  const orgUsers = useSelector((state) => state.users.users);
  const userMap = useSelector(getUserIdMap);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const needsApproval = formApprovals?.[assignedFormId]?.editableApprovals;

  const selectedFormOverride = useMemo(() => (
    childSubmitMode ? {} : selectedForm
  ), [childSubmitMode, selectedForm]);

  const latestRejectionMsg = useMemo(() => {
    const approvals = selectedFormOverride?.approvals || [];
    if (!approvals.length) return null;
    const latest = [...approvals].sort(
      (a, b) => (b.timestamp || 0) - (a.timestamp || 0),
    )[0];

    const state = (latest.state || latest.status || '').toLowerCase();
    if (state !== 'rejected') return null;
    const note = latest.note ?? '';
    return note
      ? `Form was rejected with note: ${note}`
      : 'Form was rejected';
  }, [selectedFormOverride?.approvals]);

  const timeEntryCardSections = useMemo(() => {
    const allSections = [];
    Object.values(timeEntryCardCustomFields).forEach(({ fields = [] }) => {
      allSections.push(...fields);
    });

    return allSections;
  }, [timeEntryCardCustomFields]);

  const {
    manual: manualDrafts = {},
    assigned: assignedDrafts = {},
  } = useSelector((state) => state.forms.drafts);
  const {
    company: {
      settings = {},
      companyImageURL: logo,
    } = {},
  } = useSelector((state) => state.settings);

  const { id: selectedFormId } = selectedFormOverride || {};

  useEffect(() => {
    if (visible && assignedFormId) {
      dispatch(getSharedFormDraftChildren(assignedFormId));
    }
  }, [assignedFormId, visible]);

  // Edits, resubmits and PO close reopen an initiated form, so they are not a first open
  const isEditSession = !!(isEdit || isResubmit || isOnEditStep || poCloseMode);

  useEffect(() => {
    if (visible && assignedFormId && !isEditSession && !controlledMode) {
      dispatch(markFormInitiated(assignedFormId));
    }
  }, [assignedFormId, visible, isEditSession, controlledMode]);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);

  const {
    id,
    title,
    formData = {},
    fileMap,
    initialData,
    initialCustomerIds,
    initialProjectIds,
    initialCostcodeIds,
    initialVendorIds = [],
    initialBucketIds,
    initialCardId,
    initialCardTitle,
    initialCardBoardId,
    initialEquipmentId,
    drawOptions = [],
    draftId,
    formNumber,
    formNumberProjectId,
    templateProjectId,
    templateCustomerId,
    valueFieldId,
    useStandardTemplate,
    formUsers = [],
    divisionId,
    divisionIds,
    lastUpdatedDraft,
    parentForm,
    createTimeTableIds,
    isSharedForm,
    pdfVersion,
  } = useMemo(() => {
    // Manual form template
    if (!assignedFormId && !isCopy) {
      const {
        id: templateId,
        name: templateTitle,
        number,
        fileMap: templateFileMap = {},
        formData: templateFormData = {},
        drawOptions: templateDrawOptions = [],
        projectId: templateProjectId,
        valueField: valueFieldId,
        useStandardTemplate: useStandard,
        divisionId: templateDivisionId,
        divisionIds: templateDivisionIds = [],
        createTimeTableIds: templateTimeTableIds,
        pdfVersion: templatePdfVersion,
      } = selectedFormTemplate || {};

      const {
        workflow: {
          isSharedForm = false,
        } = {},
      } = templateFormData;

      const {
        [templateProjectId]: { customerId: templateCustomerId } = {},
      } = projectIdMap;
      const {
        [assignedDraftId]: {
          id: draftId,
          customers: draftCustomerIds = [],
          vendors: draftVendorIds = [],
          data: draftData,
          lastUpdated: draftUpdateTS = 0,
          projects: draftProjectIds,
          buckets: draftBucketIds,
          cardId: draftCardId,
          number: draftNumber,
          numberProjectId: draftNumberProjectId,
        } = {},
      } = manualDrafts;

      const resolvedDraftId = assignedDraftId ?? (draftData ? draftId : null);

      const safeFileMap = initialFileMap ?? {};

      let initData = (draftData || preloadedData || undefined);
      let initFileMap = {
        ...safeFileMap,
        ...templateFileMap,
      };
      if (controlledMode) {
        const { data: controlledInitData, fileMap: controlledFileMap = {} } = controlledData;
        initData = controlledInitData;
        initFileMap = {
          ...initFileMap,
          ...controlledFileMap,
        };
      }

      const {
        customerIds: preloadedCustomerIds,
        projectIds: preloadedProjectIds,
        vendorId: preloadedVendorId,
        costcodeId: preloadedCostcodeId,
        parentFormId: preloadedParentFormId,
        parentTemplateId: preloadedParentTemplateId,
        bucketIds: preloadedBucketIds,
      } = preloadedIds || {};

      const newSections = FormHelpers.createDuplicateSectionsFromDraftData(
        templateFormData,
        initData,
      );

      const resolvedDivisionIds = resolveFormDivisionIds({
        divisionIds: templateDivisionIds,
        divisionId: templateDivisionId,
      });
      const selectedTemplateProjectId = templateProjectId ? [templateProjectId] : [];
      return {
        id: templateId,
        title: createTitle({
          mode,
          title: templateTitle,
          number: draftNumber ?? number,
        }),
        formNumber: draftNumber ?? number,
        formNumberProjectId: draftNumberProjectId ?? null,
        formData: {
          ...templateFormData,
          sections: newSections,
        },
        drawOptions: templateDrawOptions,
        fileMap: initFileMap,
        initialData: initData,
        draftId: resolvedDraftId,
        initialProjectIds: preloadedProjectIds ?? draftProjectIds ?? selectedTemplateProjectId,
        initialVendorIds: preloadedVendorId ? [preloadedVendorId] : draftVendorIds,
        initialCostcodeIds: preloadedCostcodeId ? [preloadedCostcodeId] : [],
        initialCustomerIds: preloadedCustomerIds ?? draftCustomerIds ?? templateCustomerId,
        initialCostcodeId: preloadedCostcodeId,
        initialBucketIds: preloadedBucketIds ?? draftBucketIds,
        templateProjectId,
        templateCustomerId,
        valueFieldId,
        useStandardTemplate: useStandard,
        divisionId: resolvedDivisionIds[0] ?? templateDivisionId,
        divisionIds: resolvedDivisionIds,
        lastUpdatedDraft: draftUpdateTS,
        parentForm: childSubmitMode ? {
          id: preloadedParentFormId,
          templateId: preloadedParentTemplateId,
        } : undefined,
        initialCardId: draftCardId,
        createTimeTableIds: templateTimeTableIds,
        isSharedForm: isSharedForm && !assignedDraftId,
        pdfVersion: templatePdfVersion,
      };
    }
    // Assigned form, with data (maybe)
    const {
      templateId,
      templateName,
      templateSchema = {},
      data = {},
      fileMap: finishedMap,
      customers: formCustomers = [],
      projects: formProjects = [],
      costcodes: formCostcodes = [],
      drawOptions: formDrawOptions = [],
      number: sourceNumber,
      lastUpdated: formUpdateTS = 0,
      useStandardTemplate: useStandard,
      valueField: valueFieldId,
      users,
      divisionId: formDivisionId,
      divisionIds: formDivisionIds = [],
      buckets: formBuckets = [],
      cardId: formCardId,
      cardTitle: formCardTitle,
      boardId: formCardBoardId,
      equipmentId: formEquipmentId,
      formValue,
      costToDate,
      createTimeTableIds: formTimeTableIds,
      pdfVersion: formPdfVersion,
      templateProjectId: templateProjectIdOverride,
    } = selectedFormOverride;
    // A copy is a new form, so it must not inherit the source form's number
    const number = isCopy ? null : sourceNumber;
    const {
      divisionIds: templateDivisionIds = [],
      divisionId: templateDivisionId,
    } = selectedFormTemplate || {};
    const resolvedDivisionIds = resolveFormDivisionIds(
      { divisionIds: formDivisionIds, divisionId: formDivisionId },
      { divisionIds: templateDivisionIds, divisionId: templateDivisionId },
    );
    const {
      [assignedFormId]: {
        id: draftId,
        data: draftData = data,
        lastUpdated: draftUpdateTS = 0,
        projects: draftProjects = [],
        customers: draftCustomers = [],
        buckets: draftBuckets = [],
        cardId: draftCardId,
        number: draftNumber,
        numberProjectId: draftNumberProjectId,
      } = {},
    } = assignedDrafts;

    const resolvedAssignedDraftId = draftData === data ? null : draftId;

    const fullCostcodeIds = formCostcodes?.map(({
      costcodeId: formCostcodeId,
      phaseId: formPhaseId,
    }) => (
      formCostcodeId ? `${formPhaseId ?? 'unphased'}.${formCostcodeId}` : null
    )).filter((val) => val);

    const shouldUseDraft = !isEdit && !isResubmit && draftUpdateTS > formUpdateTS;

    const existingCircularFieldIds = draftData?.circularFieldIds ?? [];
    if (circularFieldIds.length) {
      const newSections = FormHelpers.injectConfigIntoSections({
        sections: templateSchema?.sections,
        fieldTypes: ['table'],
        dataTypes: ['Materials', 'Equipment'],
        ids: existingCircularFieldIds,
        config: { preventEdits: true },
      });
      templateSchema.sections = newSections;
    }

    const newSections = FormHelpers.createDuplicateSectionsFromDraftData(templateSchema, draftData);
    return {
      id: templateId,
      title: createTitle({
        mode,
        title: templateName,
        number,
        formValue,
        costToDate,
        poCloseMode,
      }),
      formData: {
        ...templateSchema,
        sections: newSections,
      },
      fileMap: finishedMap,
      initialData: (shouldUseDraft ? draftData : data) ?? {},
      initialCustomerIds: shouldUseDraft ? draftCustomers : formCustomers,
      initialProjectIds: shouldUseDraft ? draftProjects : formProjects,
      initialCostcodeIds: fullCostcodeIds,
      initialBucketIds: shouldUseDraft ? draftBuckets : formBuckets,
      formNumber: shouldUseDraft ? (draftNumber ?? number) : number,
      formNumberProjectId: shouldUseDraft ? (draftNumberProjectId ?? null) : null,
      drawOptions: formDrawOptions,
      draftId: shouldUseDraft ? resolvedAssignedDraftId : null,
      useStandardTemplate: useStandard,
      valueFieldId,
      formUsers: users,
      divisionId: resolvedDivisionIds[0] ?? formDivisionId ?? templateDivisionId,
      divisionIds: resolvedDivisionIds,
      lastUpdatedDraft: shouldUseDraft ? draftUpdateTS : formUpdateTS,
      initialCardId: shouldUseDraft ? draftCardId : formCardId,
      initialCardTitle: shouldUseDraft ? null : formCardTitle,
      initialCardBoardId: shouldUseDraft ? null : formCardBoardId,
      initialEquipmentId: shouldUseDraft ? null : formEquipmentId,
      createTimeTableIds: formTimeTableIds,
      pdfVersion: formPdfVersion,
      templateProjectId: templateProjectIdOverride,
    };
  }, [
    assignedFormId,
    assignedDraftId,
    selectedFormTemplate,
    selectedFormOverride,
    assignedDrafts,
    manualDrafts,
    controlledData,
    controlledMode,
    projectIdMap,
    preloadedData,
    preloadedIds,
    mode,
    initialFileMap,
    childSubmitMode,
    poCloseMode,
    isCopy,
    isEdit,
    isResubmit,
  ]);

  const {
    sections: formSections = [],
    collected: {
      employeeSignature: { collect: collectSig } = {},
    } = {},
  } = formData;

  const [responses, setResponses] = useState({});
  const [submitTried, setSubmitTried] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState();
  const [loading, setLoading] = useState();
  const [customerIds, setCustomerIds] = useState(initialCustomerIds?.length
    ? initialCustomerIds
    : []);
  const [projectIds, setProjectIds] = useState(initialProjectIds?.length
    ? initialProjectIds
    : [lockedProjectId]);
  const [bucketIds, setBucketIds] = useState(initialBucketIds);
  const [vendorId, setVendorId] = useState(initialVendorIds?.[0] ?? null);
  const [showSigModal, setShowSigModal] = useState(false);
  const [lastDraft, setLastDraft] = useState();
  const [showReassignDrawer, setShowReassignDrawer] = useState();
  const [fileIsPreview, setFileIsPreview] = useState(false);
  const [sections, setSections] = useState(formSections);
  const [autosaveEnabled, setAutosaveEnabled] = useState(true); // if autosave is enabled
  const [shouldAutosave, setShouldAutosave] = useState(false); // toggle time interval autosaves
  const [isAutosaving, setIsAutosaving] = useState(false); // if autosave is in progress
  const [sessionDraftId, setSessionDraftId] = useState(uuid());
  const [initiatedOn, setInitiatedOn] = useState(null);
  const [reservedNumber, setReservedNumber] = useState();
  const [numberProjectId, setNumberProjectId] = useState(null);
  const reserveOrSubmissionInFlightRef = useRef(false);
  const lastReserveKeyRef = useRef(null);
  const userChangedProjectRef = useRef(false);
  const [isDuplicateSliderVisible, setIsDuplicateSliderVisible] = useState(false);
  const [isConfigureSearchActive, setIsConfigureSearchActive] = useState(false);
  const [fieldsInitialLoaded, setFieldsInitialLoaded] = useState(false);

  const currentUser = useSelector((state) => state.profile.profile);

  const reassignmentTemplate = useMemo(
    () => (id && templates[id]) || selectedFormTemplate,
    [templates, id, selectedFormTemplate],
  );

  const reassignmentConfig = useMemo(() => getReassignmentConfigFromTemplate({
    ...reassignmentTemplate,
    formData,
  }), [reassignmentTemplate, formData]);

  const positionId = userMap[Permissions.id]?.positionId ?? null;

  const canShowReassign = useMemo(() => {
    if (poCloseMode || !(assignedFormId || assignedDraftId)) return false;
    if (assignedFormId) {
      return canReassignForm({
        config: reassignmentConfig,
        userId: Permissions.id,
        positionId,
        formUsers,
      });
    }
    return canReassignDraft({
      config: reassignmentConfig,
      userId: Permissions.id,
      positionId,
    });
  }, [
    poCloseMode,
    assignedFormId,
    assignedDraftId,
    reassignmentConfig,
    positionId,
    formUsers,
  ]);

  const assignOwn = getReassignmentAssignment({
    config: reassignmentConfig,
    positionId,
  }) === ASSIGN_OWN;

  const {
    duplicateCount,
    hasDuplicateDetection,
    allowConfigureConflictDetection,
    showInDetection,
    getConfigureSearchInitialQuery,
    searchBoardsForDuplicates,
    relativeDateAnchors,
  } = useDuplicateCardDetection({
    formData,
    responses,
    visible,
    isConfigureSearchActive,
    poCloseMode,
  });

  const showConflictButton = hasDuplicateDetection && (
    allowConfigureConflictDetection || duplicateCount > 0
  );

  const sectionPermissionMap = useMemo(() => getSectionPermissionMap({
    sections,
    users: orgUsers,
    positionNames,
    userId: Permissions.id,
  }), [sections, orgUsers, positionNames]);

  const effectiveFormNumber = reservedNumber !== undefined ? reservedNumber : formNumber;

  const displayTitle = useMemo(() => {
    if (assignedFormId || isCopy) return title;
    const templateTitle = selectedFormTemplate?.name;
    return createTitle({
      mode,
      title: templateTitle,
      number: effectiveFormNumber,
    });
  }, [
    assignedFormId,
    isCopy,
    title,
    selectedFormTemplate?.name,
    effectiveFormNumber,
    mode,
  ]);

  const shouldReserveFormNumber = useMemo(() => (
    !assignedFormId
    && !isCopy
    && !isExternalForm
    && !controlledMode
    && visible
    && selectedFormTemplate?.formData?.collected?.number?.collect
    && settings?.generateFormNumberInDraft?.[selectedFormTemplate?.typeId]
  ), [
    assignedFormId,
    isCopy,
    isExternalForm,
    controlledMode,
    visible,
    selectedFormTemplate,
    settings,
  ]);

  const effectiveReserveProjectId = useMemo(() => (
    projectIds?.find(Boolean) ?? null
  ), [projectIds]);

  const expectedReserveProjectId = useMemo(() => (
    initialProjectIds?.find(Boolean) ?? lockedProjectId ?? null
  ), [initialProjectIds, lockedProjectId]);

  const isProjectUniqueFormNumber = useMemo(() => (
    !!settings?.formAnchorsUnique?.[selectedFormTemplate?.typeId]
  ), [settings, selectedFormTemplate?.typeId]);

  const getReserveKey = useCallback((projectId) => (
    isProjectUniqueFormNumber ? (projectId ?? '') : ''
  ), [isProjectUniqueFormNumber]);

  useEffect(() => {
    if (!visible || !shouldReserveFormNumber) return;

    // If project id hasn't changed, don't reserve a new number
    const reserveKey = getReserveKey(effectiveReserveProjectId);
    if (lastReserveKeyRef.current === reserveKey || reserveOrSubmissionInFlightRef.current) {
      return;
    }

    const existingDraftNumber = draftId && formNumber;
    if (existingDraftNumber && lastReserveKeyRef.current === null) {
      lastReserveKeyRef.current = getReserveKey(
        formNumberProjectId ?? expectedReserveProjectId ?? effectiveReserveProjectId,
      );
      return;
    }

    const reserveFormNumber = async () => {
      reserveOrSubmissionInFlightRef.current = true;

      const { data } = await FormsService.reserveFormNumber({
        templateId: id,
        projectIds: effectiveReserveProjectId ? [effectiveReserveProjectId] : [],
        draftId: draftId || undefined,
      });
      reserveOrSubmissionInFlightRef.current = false;
      if (!data) return;
      lastReserveKeyRef.current = reserveKey;
      setReservedNumber(data.number);
      setNumberProjectId(data.numberProjectId);
    };

    reserveFormNumber();
  }, [
    shouldReserveFormNumber,
    id,
    effectiveReserveProjectId,
    expectedReserveProjectId,
    draftId,
    formNumber,
    formNumberProjectId,
    getReserveKey,
    visible,
    isProjectUniqueFormNumber,
  ]);

  useEffect(() => {
    setSections(formSections);
  }, [formData]);

  const onDownload = useCallback(async () => {
    await downloadFile({ fileObject: selectedFile });
  }, [selectedFile]);

  const onShowReassignClicked = useCallback(() => setShowReassignDrawer(true), []);
  const onCloseReassign = useCallback(() => setShowReassignDrawer(false), []);

  const closeSigModal = useCallback(() => setShowSigModal(false), []);
  const onCloseFile = useCallback(() => {
    setSelectedFile();
    setFileIsPreview(false);
  }, []);

  const selectedFileType = useMemo(() => (selectedFile
    ? getFileType(selectedFile)
    : null
  ), [selectedFile]);

  const onPreviewClicked = useCallback(async () => {
    setFileIsPreview(true);
    setSelectedFile(await generatePDFPreview({
      title: displayTitle,
      formData: {
        ...formData,
        sections,
      },
      useStandardTemplate,
      drawOptions,
      currentUser,
      sections,
      responses,
      fileMap,
      formNumber: effectiveFormNumber,
      settings,
      projectIdMap,
      equipmentIdMap: equipmentMap,
      logo,
      pdfVersion,
    }));
  }, [
    responses,
    sections,
    useStandardTemplate,
    formData,
    drawOptions,
    currentUser,
    displayTitle,
    fileMap,
    effectiveFormNumber,
    settings,
    equipmentMap,
    logo,
    formSections,
    pdfVersion,
  ]);

  const cleanup = useCallback(() => {
    reserveOrSubmissionInFlightRef.current = true;
    setResponses({});
    setSubmitTried(false);
    setCustomerIds();
    setProjectIds();
    setBucketIds();
    setVendorId();
    setShowSigModal(false);
    onClose();
    setLastDraft();
    setAutosaveEnabled(false);
    setShouldAutosave(false);
    userChangedProjectRef.current = false;
    dispatch(clearFormSelection());
  }, [dispatch, onClose]);

  const handleRedirect = useCallback(() => {
    onClose();
  });

  const onSaveDraft = useCallback(async (keepOpen, isAutosave = false) => {
    if (isAutosave) {
      message.info('Autosaving...');
      setIsAutosaving(true);
    }
    const {
      responses: clearedResponses,
      hiddenFields,
    } = applyConditionalResponseCleanup({ responses, sections, sectionPermissionMap });
    const payload = prepareResponsePayload({
      sections,
      responses: clearedResponses,
      title,
      templateId: id,
      assignedFormId,
      assignedDraftId: draftId,
      projectIds,
      bucketIds,
      customerIds,
      vendorId,
      cardId: cardId ?? initialCardId,
      equipmentId: equipmentId ?? initialEquipmentId,
      hiddenFields,
    });
    payload.isDraft = true;
    payload.isAutosave = isAutosave;
    payload.sessionDraftId = sessionDraftId;
    payload.number = effectiveFormNumber;
    payload.numberProjectId = numberProjectId;
    if (!isEditSession) payload.initiatedOn = initiatedOn;
    if (await dispatch(submitForm(payload, false, { circularFieldIds }))) {
      if (keepOpen) {
        setLastDraft(clearedResponses);
      } else {
        cleanup();
      }
    }
    setIsAutosaving(false);
  }, [
    sections,
    responses,
    title,
    id,
    draftId,
    assignedFormId,
    dispatch,
    cleanup,
    projectIds,
    bucketIds,
    customerIds,
    vendorId,
    cardId,
    sessionDraftId,
    initialCardId,
    initialEquipmentId,
    sectionPermissionMap,
    effectiveFormNumber,
    numberProjectId,
    isEditSession,
    initiatedOn,
  ]);

  const { dataChanged, autosaveDataChanged } = useMemo(() => {
    const baseConditional = (
      visible
      && !controlledMode
      && !needsApproval
    );

    if (!baseConditional) {
      return {
        dataChanged: false,
        autosaveDataChanged: false,
      };
    }

    // If we have a lastDraft, compare current responses with it
    // Otherwise, compare with initialData
    const comparisonData = lastDraft ? {
      sections: Object.keys(lastDraft).map((fieldId) => ({
        fields: [{ fieldId, response: lastDraft[fieldId] }],
      })),
    } : (initialData || {});

    return {
      dataChanged: formsValuesAreDifferent({
        initialData: comparisonData,
        fileMap,
        sections,
        responses,
      }),
      autosaveDataChanged: formsValuesAreDifferent({
        initialData: comparisonData,
        fileMap,
        sections,
        responses,
        defaultReturn: false,
      }),
    };
  }, [
    visible,
    needsApproval,
    controlledMode,
    initialData,
    fileMap,
    sections,
    responses,
    lastDraft,
  ]);

  const onSaveDraftClicked = useCallback((keepOpen) => {
    CustomConfirmModal({
      title: 'Save a draft?',
      content: (
        <span>
          Click
          {' '}
          <b style={{ fontWeight: 1000, color: 'black' }}>Yes</b>
          {' '}
          to keep a draft of your work.
        </span>
      ),
      okText: 'Yes',
      cancelText: 'No',
      onCancel: keepOpen ? null : cleanup,
      onOk: () => onSaveDraft(keepOpen),
    });
  }, [cleanup, onSaveDraft]);

  const onCloseClicked = useCallback(() => {
    if (
      !dataChanged
      || controlledMode
      || draftDisabled
      || poCloseMode
      || loading
      || isEdit
      || isResubmit
    ) return cleanup();
    return onSaveDraftClicked(false);
  }, [
    controlledMode,
    dataChanged,
    onSaveDraftClicked,
    draftDisabled,
    cleanup,
    poCloseMode,
    loading,
    isEdit,
    isResubmit,
  ]);

  const onDraftDelete = useCallback(() => {
    CustomConfirmModal({
      title: 'Clear the draft?',
      content: 'Clearing a draft will delete the draft permanently and disable auto-save functionality',
      okText: 'Clear',
      cancelText: 'No',
      onOk: async () => {
        if (await dispatch(deleteDraft({ id: draftId, formId: assignedFormId }))) {
          setAutosaveEnabled(false);

          // Manual form path - use selectedFormTemplate and preloadedIds
          if (!assignedFormId && !isCopy) {
            const {
              fileMap: templateFileMap = {},
              projectId: origTemplateProjectId,
            } = selectedFormTemplate || {};
            const {
              customerIds: preloadedCustomerIds,
              projectIds: preloadedProjectIds,
            } = preloadedIds || {};

            // Use preloaded IDs if available, otherwise fall back to template
            const resetProjectId = preloadedProjectIds?.[0] ?? origTemplateProjectId;
            const { [resetProjectId]: { customerId: derivedCustomerId } = {} } = projectIdMap;
            const resetCustomerId = preloadedCustomerIds?.[0] ?? derivedCustomerId;

            const originalResponses = preparePreloadedFormTriggerData({
              initialData: undefined,
              fileMap: templateFileMap,
              sections,
              templateProjectId: resetProjectId,
              templateCustomerId: resetCustomerId,
              projectIdMap,
              customerIdMap: customers,
              equipmentIdMap: equipmentMap,
              sectionPermissionMap,
            });
            setResponses(originalResponses);
            return;
          }

          // Assigned form path - use selectedFormOverride
          const {
            data = {},
            fileMap: selectedFileMap = {},
            projects: formProjects = [],
            customers: formCustomers = [],
          } = selectedFormOverride ?? {};
          if (!data || !selectedFileMap) {
            setResponses({});
            return;
          }
          const originalResponses = preparePreloadedFormTriggerData({
            initialData: data,
            fileMap: selectedFileMap,
            sections,
            templateProjectId: formProjects?.[0],
            templateCustomerId: formCustomers?.[0],
            projectIdMap,
            customerIdMap: customers,
            equipmentIdMap: equipmentMap,
            sectionPermissionMap,
          });
          setResponses(originalResponses);
        }
      },
    });
  }, [
    dispatch,
    draftId,
    assignedFormId,
    isCopy,
    preloadedIds,
    selectedFormTemplate,
    selectedFormOverride,
    sections,
    projectIdMap,
    customers,
    equipmentMap,
    sectionPermissionMap,
  ]);

  const onFieldTrigger = useCallback(async ({
    form,
    users,
    fieldMappings,
    sharedForm,
  }) => {
    const relevantFieldMappings = fieldMappings[form] ?? {};
    const fieldsToInclude = new Set(Object.keys(relevantFieldMappings));
    users?.forEach((user) => {
      if (typeof user === 'string' && user?.startsWith('field-')) {
        fieldsToInclude.add(user);
      }
    });
    const preparedResponse = prepareResponsePayload({
      sections,
      responses,
      title,
      templateId: id,
      fieldsToInclude,
    });
    const { data, files } = await constructFormPayloadForAPI({
      form: {
        id,
        ...preparedResponse,
      },
      isExternalForm: false,
    }) ?? {};
    const payload = {
      form,
      users,
      fieldMappings,
      sharedForm,
      data,
      files,
      sourceTemplateId: id,
    };
    try {
      const res = await Axios.post('/forms/fieldTrigger/', payload);
      return res;
    } catch (err) {
      return false;
    }
  }, [
    sections,
    responses,
    title,
    id,
  ]);

  const tryToSubmitForm = useCallback(async (signatureFile) => {
    let signatureMetadata;
    if (signatureFile) {
      signatureMetadata = {
        timestamp: DateTime.local().toMillis(),
        timezone: DateTime.local().zoneName,
      };
    }

    const {
      responses: clearedResponses,
      hiddenFields,
    } = applyConditionalResponseCleanup({ responses, sections, sectionPermissionMap });
    const isEditing = isEdit || isResubmit || isOnEditStep;
    const payload = prepareResponsePayload({
      sections,
      responses: clearedResponses,
      title,
      templateId: id,
      assignedFormId,
      assignedDraftId,
      projectId: isEditing ? (templateProjectId || lockedProjectId) : undefined,
      projectIds: isEditing ? [] : projectIds,
      bucketIds,
      customerIds,
      cardId: cardId ?? initialCardId,
      equipmentId: equipmentId ?? initialEquipmentId,
      valueFieldId,
      hiddenFields,
    });
    // Set on the payload itself so the external form's controlledSubmit forwards it too
    if (!isEditSession && !controlledMode) payload.initiatedOn = initiatedOn;

    const finalSubmit = async ({ dataOnly, clearData } = {}) => {
      if (controlledMode || isExternalForm) {
        return controlledSubmit(payload);
      }

      reserveOrSubmissionInFlightRef.current = true;

      setLoading(true);
      const cfIds = circularFieldIds?.length
        ? circularFieldIds
        : selectedForm?.data?.circularFieldIds;
      const res = await dispatch(submitForm(
        {
          ...payload,
          isOnEditStep,
          isEdit,
          isResubmit,
          signatureFile,
          signatureMetadata,
          dataOnly,
          clearData,
          poCloseMode,
          number: effectiveFormNumber,
          numberProjectId,
          reservedNumber: shouldReserveFormNumber ? effectiveFormNumber : undefined,
        },
        false,
        { circularFieldIds: cfIds },
      ));
      setLoading(false);
      if (res) {
        if (history && !poCloseMode) {
          history.replace?.(redirectUrl);
        }
        cleanup();
      }

      reserveOrSubmissionInFlightRef.current = false;
      return Promise.resolve();
    };

    const executeSubmit = async () => {
      if (isEdit) {
        await finalSubmit({ dataOnly: true });
      } else if (isResubmit) {
        ResubmitModal(finalSubmit, !!triggeredForms.length);
      } else {
        await finalSubmit({ dataOnly: false });
      }
    };

    const areTimeEntryTablesValid = validateTimeEntryTableResponses({ sections, responses });
    if (!areTimeEntryTablesValid) {
      return CustomConfirmModal({
        title: 'Warning',
        content: 'A time entry exceeds the warning threshold of hours configured for this form. Please review the entries and make sure they are correct.',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: async () => {
          await executeSubmit();
        },
      });
    }

    await executeSubmit();
  }, [
    id,
    title,
    sections,
    responses,
    assignedFormId,
    assignedDraftId,
    isOnEditStep,
    isEdit,
    isResubmit,
    controlledMode,
    controlledSubmit,
    projectIds,
    bucketIds,
    customerIds,
    triggeredForms,
    cleanup,
    valueFieldId,
    isExternalForm,
    cardId,
    equipmentId,
    history,
    initialCardId,
    initialEquipmentId,
    poCloseMode,
    templateProjectId,
    lockedProjectId,
    sectionPermissionMap,
    shouldReserveFormNumber,
    draftId,
    effectiveFormNumber,
    numberProjectId,
    initiatedOn,
    isEditSession,
    dispatch,
    circularFieldIds,
    selectedForm,
    redirectUrl,
  ]);

  const {
    errorMap,
    wentThroughFields,
  } = useMemo(() => (
    getErrorsFromResponses({
      sections,
      responses,
      sectionPermissionMap,
      companySettings: settings,
      timeEntryCardSections,
      timeEntryCardCustomFields,
      pdfVersion,
      isApproval: needsApproval,
    })
  ), [
    sections,
    responses,
    sectionPermissionMap,
    settings,
    timeEntryCardSections,
    timeEntryCardCustomFields,
    pdfVersion,
    needsApproval,
  ]);

  const onSubmitFormClicked = useCallback(async () => {
    setAutosaveEnabled(false);
    if (controlledMode) {
      if (await tryToSubmitForm()) {
        cleanup();
      }
      return;
    }
    setSubmitTried(true);
    if (!wentThroughFields || Object.keys(errorMap).length) return;
    if (collectSig) {
      setShowSigModal(true);
      return;
    }
    tryToSubmitForm();
  }, [tryToSubmitForm, collectSig, controlledMode, cleanup, errorMap, wentThroughFields]);

  useEffect(() => {
    if (submitTried) setErrors(errorMap);
  }, [submitTried, errorMap]);

  const onProjectIdsChanged = useCallback((newProjectIds) => {
    userChangedProjectRef.current = true;
    setProjectIds(newProjectIds);
  }, []);

  useEffect(() => {
    if (visible) setCustomerIds(initialCustomerIds?.length ? initialCustomerIds : []);
  }, [visible, initialCustomerIds]);

  useEffect(() => {
    if (!visible) {
      userChangedProjectRef.current = false;
      return;
    }
    if (userChangedProjectRef.current) return;
    if (initialProjectIds?.length) {
      setProjectIds(initialProjectIds);
    } else if (lockedProjectId) {
      setProjectIds([lockedProjectId]);
    }
  }, [visible, initialProjectIds, lockedProjectId]);

  useEffect(() => {
    if (visible) setVendorId(initialVendorIds?.[0]);
  }, [visible, initialVendorIds]);

  useEffect(() => {
    if (visible) setBucketIds(initialBucketIds);
  }, [visible, initialBucketIds]);

  useEffect(() => {
    if (!visible) return;
    setAutosaveEnabled(!isSharedForm);
  }, [visible, isSharedForm]);

  useEffect(() => {
    if (visible) {
      setInitiatedOn(Date.now());
    } else {
      setInitiatedOn(null);
      setResponses({});
      setSubmitTried(false);
      setErrors({});
      setLastDraft();
      setAutosaveEnabled(false);
      setShouldAutosave(false);
      setSessionDraftId(uuid());
      setReservedNumber(undefined);
      setNumberProjectId(null);
      reserveOrSubmissionInFlightRef.current = false;
      lastReserveKeyRef.current = null;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const autosaveIntervalId = setInterval(() => {
      setShouldAutosave(true);
    }, AUTOSAVE_INTERVAL);

    /* eslint-disable consistent-return */
    return () => {
      setShouldAutosave(false);
      clearInterval(autosaveIntervalId);
    };
    /* eslint-enable consistent-return */
  }, [visible]);

  useEffect(() => {
    if (!visible || !fieldsInitialLoaded) return;

    const {
      responses: newResponses,
      didResponseChange,
    } = applyConditionalResponseCleanup({ responses, sections, sectionPermissionMap });

    if (didResponseChange) {
      setResponses(newResponses);
    }
  }, [visible, responses, sections, sectionPermissionMap, fieldsInitialLoaded]);

  useEffect(() => {
    const autosaveConditions = visible
      && !poCloseMode
      && !draftDisabled
      && !isEdit
      && !isResubmit
      && shouldAutosave
      && autosaveDataChanged
      && autosaveEnabled
      && !isAutosaving
      // Avoid wiping conflict results while reviewing duplicates / configure search
      && !isDuplicateSliderVisible
      && !isConfigureSearchActive;

    if (autosaveConditions) {
      onSaveDraft(true, true);
    }
    setShouldAutosave(false);
  }, [
    visible,
    poCloseMode,
    draftDisabled,
    isEdit,
    isResubmit,
    shouldAutosave,
    autosaveDataChanged,
    autosaveEnabled,
    onSaveDraft,
    isAutosaving,
    isDuplicateSliderVisible,
    isConfigureSearchActive,
  ]);

  const divisionList = useMemo(
    () => resolveFormDivisionIds({ divisionIds, divisionId }),
    [divisionIds, divisionId],
  );
  const Container = showAsContainer ? React.Fragment : Drawer;

  const lastUpdatedDraftString = lastUpdatedDraft
    ? DateTime.fromMillis(lastUpdatedDraft).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
    : null;

  const submitTitle = useMemo(() => {
    if (isEdit || isOnEditStep) {
      return 'Submit Edit';
    }
    if (isResubmit) {
      return 'Resubmit';
    }
    return 'Submit';
  }, [isEdit, isOnEditStep, isResubmit]);

  const safeInitialCustomerIds = useMemo(() => (
    initialCustomerIds?.length ? initialCustomerIds : []
  ), [initialCustomerIds]);

  const safeInitialProjectIds = useMemo(() => {
    if (initialProjectIds?.length) return initialProjectIds;
    if (lockedProjectId) return [lockedProjectId];
    return [];
  }, [initialProjectIds, lockedProjectId]);

  const safeInitialCostcodeIds = useMemo(() => (
    initialCostcodeIds?.length ? initialCostcodeIds : []
  ), [initialCostcodeIds]);

  const safeInitialVendorIds = useMemo(() => (
    initialVendorIds?.length ? initialVendorIds : []
  ), [initialVendorIds]);

  const safeInitialBucketId = useMemo(() => (
    initialBucketIds?.length ? initialBucketIds[0] : null
  ), [initialBucketIds]);

  /**
   * HARBOUR-4930
   * Handles race condition with PO/Sub-Contract form responder mode
   * Where template is retrieved after preloading response
   * in FormResponseFields
   */
  const showFormResponseFields = (
    visible && (
      mode === DEFAULT_FORM_RESPONDER_MODE
      || (mode !== DEFAULT_FORM_RESPONDER_MODE && hasLoadedPayableTemplate)
    )
  );

  const ourInitialEquipmentId = equipmentId || initialEquipmentId;

  return (
    <>
      <Container
        push={false}
        title={
         (
           <>
             <span>{displayTitle}</span>
             {latestRejectionMsg && (
             <span
               style={{
                 marginTop: 4,
                 marginLeft: 20,
                 color: 'red',
                 fontSize: 14,
                 fontWeight: 600,
               }}
             >
               {latestRejectionMsg}
             </span>
             )}
             {showConflictButton && (
               <DuplicateConflictButton
                 duplicateCount={duplicateCount}
                 onClick={() => setIsDuplicateSliderVisible(true)}
                 style={CONFLICT_BUTTON_STYLE}
               />
             )}
           </>
        )
       }
        width={childSubmitMode ? '55vw' : '90vw'}
        visible={visible}
        maskClosable={false}
        onClose={onCloseClicked}
        bodyStyle={{
          padding: '24px',
        }}
        keyboard={!cardId && !selectedFile}
        placement="right"
        mask={!childSubmitMode}
      >
        {formSelector ?? null}
        { id && (
          <FormResponseFields
            mode={mode}
            errors={errors}
            sections={sections}
            visible={showFormResponseFields}
            responses={responses}
            onResponsesChanged={setResponses}
            onResponseSectionsChanged={setSections}
            initialData={initialData}
            fileMap={fileMap}
            initialCustomerIds={safeInitialCustomerIds}
            initialProjectIds={safeInitialProjectIds}
            initialCostcodeIds={safeInitialCostcodeIds}
            initialVendorIds={safeInitialVendorIds}
            initialBucketId={safeInitialBucketId}
            initialCardId={initialCardId}
            initialCardTitle={initialCardTitle}
            initialCardBoardId={initialCardBoardId}
            initialEquipmentId={ourInitialEquipmentId}
            initialEquipmentTitle={equipmentMap[ourInitialEquipmentId]?.name}
            templateProjectId={templateProjectId || lockedProjectId}
            templateCustomerId={templateCustomerId}
            selectedFormId={selectedFormId}
            onCustomerIdChanged={setCustomerIds}
            onProjectIdChanged={onProjectIdsChanged}
            onVendorIdChanged={setVendorId}
            onBucketIdChanged={setBucketIds}
            style={formSelector ? { position: 'static' } : null}
            divisions={divisionList}
            isExternalForm={isExternalForm}
            header={header}
            templateId={id}
            sectionPermissionMap={sectionPermissionMap}
            parentForm={parentForm}
            createTimeTableIds={createTimeTableIds}
            onFieldTrigger={onFieldTrigger}
            formUserIds={formUsers}
            onFieldsInitialLoadedChange={setFieldsInitialLoaded}
            poCloseMode={poCloseMode}
          />
        )}
        {needsApproval ? (
          <DrawerFooter>
            <FormApprovalButton
              formId={assignedFormId}
              type={REJECT_FORM_SUBMISSION}
              redirectToHome={handleRedirect}
              collectedData={initialData?.collected}
              updateData={{
                sections,
                responses,
                title,
                templateId: id,
                assignedFormId,
                assignedDraftId,
                projectIds,
                bucketIds,
                customerIds,
                cardId: cardId ?? initialCardId,
                valueFieldId,
                equipmentId: equipmentId ?? initialEquipmentId,
              }}
              errorMap={errorMap}
            />
            <FormApprovalButton
              formId={assignedFormId}
              type={APPROVE_FORM_SUBMISSION}
              redirectToHome={handleRedirect}
              collectedData={initialData?.collected}
              updateData={{
                sections,
                responses,
                title,
                templateId: id,
                assignedFormId,
                assignedDraftId,
                projectIds,
                bucketIds,
                customerIds,
                cardId: cardId ?? initialCardId,
                valueFieldId,
                equipmentId: equipmentId ?? initialEquipmentId,
              }}
              sections={sections}
              responses={responses}
              errorMap={errorMap}
            />
          </DrawerFooter>
        ) : (
          <DrawerSubmitFooter
            onClose={hideCancelButton ? null : onCloseClicked}
            onSubmit={onSubmitFormClicked}
            loading={loading || isAutosaving}
            submitTitle={submitTitle}
            submitButtonStyle={submitButtonStyle}
            cancelButtonStyle={cancelButtonStyle}
          />
        )}
        {!previewDisabled && (
          <OnTraccrButton
            icon={<EyeOutlined />}
            title="Preview"
            onClick={onPreviewClicked}
            id="form-response-pdf-preview-button"
          />
        )}
        {draftId && !poCloseMode && (
          <OnTraccrButton
            type="back"
            icon={<UndoOutlined />}
            title="Clear Draft"
            onClick={onDraftDelete}
            id="form-response-draft-clear-button"
            loading={isAutosaving}
          />
        )}
        {draftId && lastUpdatedDraftString && (
          <div className="form-response-draft-timestamp">
            Last saved:
            {' '}
            {lastUpdatedDraftString}
          </div>
        )}
        {dataChanged && !draftDisabled && !isEdit && !isResubmit && !poCloseMode && (
          <OnTraccrButton
            type="back"
            icon={<SaveOutlined />}
            title="Save Draft"
            onClick={onSaveDraftClicked}
            id="form-response-draft-save-button"
            style={{
              right: ((isEdit || isOnEditStep) ? 212 : 182)
                + (loading ? 20 : 0)
                + (needsApproval ? 2 : 0)
                + (isResubmit ? 15 : 0)
                + (isAutosaving ? 40 : 0),
              margin: needsApproval ? 2 : 0,
            }}
            loading={isAutosaving}
          />
        )}
        {canShowReassign && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '100px',
            }}
          >
            <OnTraccrButton title="Reassign Form" onClick={onShowReassignClicked} />
          </div>
        )}
        <FullPhoto
          file={selectedFile}
          rotation={selectedFile?.rotation ?? 0}
          type={selectedFileType}
          onClose={onCloseFile}
          onDownload={onDownload}
          useApryse={!fileIsPreview}
          enableEscapeToClose
        />
        <FormReassignmentDrawer
          visible={showReassignDrawer}
          assignedFormId={assignedFormId}
          assignedDraftId={assignedDraftId}
          formUsers={formUsers}
          onClose={onCloseReassign}
          onCloseForm={onClose}
          assignOwn={assignOwn}
        />
        <DuplicateCardsSlider
          visible={visible && isDuplicateSliderVisible}
          onClose={() => setIsDuplicateSliderVisible(false)}
          allowConfigureConflictDetection={allowConfigureConflictDetection}
          getConfigureSearchInitialQuery={getConfigureSearchInitialQuery}
          relativeDateAnchors={relativeDateAnchors}
          showInDetection={showInDetection}
          onResetToDefaultSearch={searchBoardsForDuplicates}
          onConfigureSearchActiveChange={setIsConfigureSearchActive}
        />
      </Container>
      <SignatureModal
        canLoadSaved={formData?.collected?.employeeSignature?.editable}
        visible={showSigModal}
        onClose={closeSigModal}
        onSubmit={tryToSubmitForm}
      />
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
FormResponder.propTypes = {
  visible: PropTypes.bool,
  mode: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  redirectUrl: PropTypes.string,
  assignedForm: PropTypes.shape({
    id: PropTypes.string,
    draftId: PropTypes.string,
  }),
  isOnEditStep: PropTypes.bool,
  isEdit: PropTypes.bool,
  isResubmit: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  controlledData: PropTypes.shape({
    data: PropTypes.shape({}),
    fileMap: PropTypes.shape({}),
  }).isRequired,
  controlledMode: PropTypes.bool.isRequired, // Used to override dispatching events from here.
  preloadedData: PropTypes.shape({}).isRequired,
  preloadedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  cardId: PropTypes.string.isRequired,
  equipmentId: PropTypes.string,
  draftDisabled: PropTypes.bool,
  formSelector: PropTypes.bool, // Allows user to select desired form
  lockedProjectId: PropTypes.string,
  showAsContainer: PropTypes.bool,
  isExternalForm: PropTypes.bool,
  previewDisabled: PropTypes.bool,
  header: PropTypes.node,
  hideCancelButton: PropTypes.bool,
  submitButtonStyle: PropTypes.shape({}),
  cancelButtonStyle: PropTypes.shape({}),
  initialFileMap: PropTypes.object,
  childSubmitMode: PropTypes.bool.isRequired,
  poCloseMode: PropTypes.bool,
  hasLoadedPayableTemplate: PropTypes.bool,
  isCopy: PropTypes.bool,
};

FormResponder.defaultProps = {
  visible: false,
  mode: DEFAULT_FORM_RESPONDER_MODE,
  history: {},
  redirectUrl: '/forms/log',
  assignedForm: {},
  draftDisabled: false,
  showAsContainer: false,
  header: null,
  equipmentId: null,
  isExternalForm: false,
  previewDisabled: false,
  isOnEditStep: false,
  isEdit: false,
  isResubmit: false,
  submitButtonStyle: {},
  cancelButtonStyle: {},
  hideCancelButton: false,
  lockedProjectId: undefined,
  formSelector: false,
  initialFileMap: null,
  poCloseMode: false,
  hasLoadedPayableTemplate: false,
  isCopy: false,
};
